import { useState, useRef, useEffect, useCallback, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import { emitTourEvent } from "@/hooks/use-guided-tour";
import { useParams, useLocation, Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetProject,
  type ProjectDetail,
  useUpdateProject,
  useDeleteProject,
  useProjectAggregates,
  useProjectQuotes,
  useProjectInvoices,
  useProjectActualCosts,
  useProjectFiles,
  useProjectActivity,
  useCreateProjectActualCost,
  useUpdateProjectActualCost,
  useDeleteProjectActualCost,
  useDetachQuote,
  useAttachQuote,
  useDetachInvoice,
  useAttachInvoice,
  useResyncProjectFromQuotes,
  useCreateProjectFile,
  useDeleteProjectFile,
  useGetProjectCoEstimate,
  useAttachCoItems,
  useCreateEstimate,
  useCreateInvoice,
  useListCustomers,
  useCreateCustomer,
  getListCustomersQueryKey,
  getProjectCoEstimateQueryKey,
  useGetEstimate,
  useListEstimates,
  useListInvoices,
  useGetSettings,
  getGetSettingsQueryKey,
  listProjectQuotes,
  listProjectInvoices,
  listProjectActualCosts,
  listProjectFiles,
  getGetProjectQueryKey,
  getProjectAggregatesQueryKey,
  getProjectDocumentLinksQueryKey,
  getGetEstimateQueryKey,
  getProjectQuotesQueryKey,
  getProjectInvoicesQueryKey,
  getProjectActualCostsQueryKey,
  getProjectFilesQueryKey,
  getProjectActivityQueryKey,
  getListProjectsQueryKey,
  type ProjectStatus,
  type ProjectCostType,
  type ProjectActualCost,
  type CreateProjectActualCostBody,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { ProGate, ProTeaserSection } from "@/components/pro-gate";
import { ArrowLeft, Trash2, Plus, RefreshCw, Upload, FileText, X, Link2, Unlink, LayoutDashboard, Receipt, DollarSign, StickyNote, Download, Loader2, Calendar as CalendarIcon, ClipboardList, ChevronDown, ChevronRight, Bold, Italic, List, ListOrdered, Heading2, Send } from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { exportProjectSummaryPDF, exportProjectArchive, type ProjectSummaryInput } from "@/lib/project-export";
import { EstimateLineItemSection } from "@/components/estimate-line-items";
import { InlineCustomerSelect } from "@/components/inline-customer-select";
import { CreateCustomerModal, type CreateCustomerFormValues } from "@/components/create-customer-modal";
import { ReceiptImport } from "@/components/receipt-import";
import { getProjectAggregates, type ProjectAggregates } from "@workspace/api-client-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";
import { useTargetMargin, TONE_HEX } from "@/hooks/use-target-margin";

// One decimal, because rounding to whole thousands gives ticks like 1950 and 2300 the same label.
const formatAxisMoney = (v: number) =>
  Math.abs(v) < 1000 ? `$${v}` : `$${parseFloat((v / 1000).toFixed(1))}k`;

const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: "lead", label: "Lead" },
  { value: "quoted", label: "Quoted" },
  { value: "approved", label: "Approved" },
  { value: "in_progress", label: "In Progress" },
  { value: "complete", label: "Complete" },
  { value: "archived", label: "Archived" },
];

const COST_TYPES = ["labour", "materials", "fees"] as const satisfies readonly ProjectCostType[];
type CatalogCostType = typeof COST_TYPES[number];
const CATALOG_COST_TYPE_SET: ReadonlySet<string> = new Set(COST_TYPES);


async function requestUploadUrl(file: File): Promise<{ uploadURL: string; objectPath: string }> {
  const res = await fetch("/api/storage/uploads/request-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type || "application/octet-stream" }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to get upload URL");
  }
  return res.json();
}

async function uploadToGcs(file: File, uploadURL: string): Promise<void> {
  const res = await fetch(uploadURL, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
  });
  if (!res.ok) throw new Error("Failed to upload file");
}

function parseISODate(s: string | null): Date | undefined {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function formatISODate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function InlineDateEditor({
  label,
  value,
  otherDate,
  isStart,
  onSave,
}: {
  label: string;
  value: string | null;
  otherDate: string | null;
  isStart: boolean;
  onSave: (next: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open, value]);

  const validate = (next: string | null): string | null => {
    if (!next || !otherDate) return null;
    if (isStart && next > otherDate) {
      return "Start date must be on or before the target completion date.";
    }
    if (!isStart && next < otherDate) {
      return "Target completion date must be on or after the start date.";
    }
    return null;
  };

  const trySave = (next: string | null) => {
    const err = validate(next);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setOpen(false);
    if (next !== (value ?? null)) onSave(next);
  };

  const triggerStyle: CSSProperties = {
    height: "auto",
    padding: "0 0.375rem",
    fontWeight: "normal",
    color: value ? "inherit" : "hsl(var(--muted-foreground))",
    fontStyle: value ? "normal" : "italic",
  };

  const selected = parseISODate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          style={triggerStyle}
          aria-label={value ? `${label}: ${value}. Click to edit.` : `Set ${label.toLowerCase()}`}
        >
          <CalendarIcon style={{ width: 12, height: 12, marginRight: 4 }} />
          {value ? `${label}: ${value}` : `Set ${label.toLowerCase()}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-3" align="start" collisionPadding={8} sideOffset={4}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-start" }}>
          <Label style={{ fontSize: "0.75rem" }}>{label}</Label>
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            captionLayout="label"
            className="w-[calc(7*var(--cell-size)+1.5rem)]"
            onSelect={(d) => {
              if (!d) return;
              trySave(formatISODate(d));
            }}
          />
          {error && (
            <p style={{ color: "hsl(var(--destructive))", fontSize: "0.75rem", margin: 0 }}>{error}</p>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
            <Button
              variant="ghost"
              size="sm"
              disabled={!value}
              onClick={() => trySave(null)}
            >
              Clear
            </Button>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function ProjectDetailPage() {
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  if (!isPro) {
    return (
      <div>
        <div style={{ marginBottom: "1rem" }}>
          <Link href="/projects">
            <Button variant="ghost" size="sm"><ArrowLeft style={{ width: 16, height: 16, marginRight: 4 }} /> All projects</Button>
          </Link>
        </div>
        <ProTeaserSection
          title="Project Tracking"
          description="Track quotes, invoices, actual costs, and profit margin for every job — all in one place."
        >
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700 }}>Smith Bathroom Remodel</h1>
          <p style={{ color: "hsl(var(--muted-foreground))" }}>Smith Family · In Progress</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.75rem", marginTop: "1rem" }}>
            {[["Quoted", "$12,400"], ["Invoiced", "$8,000"], ["Paid", "$8,000"], ["Outstanding", "$0"]].map(([label, val]) => (
              <Card key={label}>
                <CardContent style={{ padding: "1rem" }}>
                  <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{val}</div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div style={{ marginTop: "1rem", height: 160, background: "hsl(var(--muted))", borderRadius: "0.5rem" }} />
        </ProTeaserSection>
      </div>
    );
  }

  return <ProjectDetailContent />;
}

function ProjectDetailContent() {
  const params = useParams();
  const projectId = Number(params.id);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  const isMobile = useIsMobile();
  const { data: project, isLoading } = useGetProject(projectId);
  const { data: agg } = useProjectAggregates(projectId);
  const { data: companySettings } = useGetSettings({ query: { queryKey: getGetSettingsQueryKey() } });
  const [exporting, setExporting] = useState<null | "pdf" | "zip">(null);
  const [location] = useLocation();
  const tabFromUrl = new URLSearchParams(location.split("?")[1] ?? "").get("tab");
  const [activeProjectTab, setActiveProjectTab] = useState(tabFromUrl ?? "overview");

  const loadExportData = async (): Promise<ProjectSummaryInput> => {
    if (!project) throw new Error("Project not loaded");
    const [aggData, quotes, invoices, costs, files] = await Promise.all([
      (agg ?? null) as ProjectAggregates | null
        ? Promise.resolve(agg as ProjectAggregates)
        : getProjectAggregates(projectId),
      listProjectQuotes(projectId),
      listProjectInvoices(projectId),
      listProjectActualCosts(projectId),
      listProjectFiles(projectId),
    ]);
    return { project, agg: aggData, quotes, invoices, costs, files };
  };

  const runExportPdf = async () => {
    if (!project || exporting) return;
    setExporting("pdf");
    const loadingId = toast({ title: "Preparing summary PDF…" });
    try {
      const data = await loadExportData();
      exportProjectSummaryPDF(data, companySettings ?? {});
      loadingId.dismiss();
      toast({ title: "Summary PDF downloaded" });
    } catch (e) {
      loadingId.dismiss();
      toast({ title: "Failed to export summary PDF", description: (e as Error).message, variant: "destructive" });
    } finally {
      setExporting(null);
    }
  };

  const runExportZip = async () => {
    if (!project || exporting) return;
    setExporting("zip");
    const loadingId = toast({ title: "Building project archive…", description: "Fetching quotes, invoices, and files." });
    try {
      const data = await loadExportData();
      const result = await exportProjectArchive(data, companySettings ?? {});
      loadingId.dismiss();
      if (result.failures.length > 0) {
        toast({
          title: "Archive downloaded with warnings",
          description: `${result.failures.length} item(s) failed: ${result.failures.slice(0, 3).join("; ")}${result.failures.length > 3 ? "…" : ""}`,
        });
      } else {
        toast({ title: "Project archive downloaded" });
      }
    } catch (e) {
      loadingId.dismiss();
      toast({ title: "Failed to export archive", description: (e as Error).message, variant: "destructive" });
    } finally {
      setExporting(null);
    }
  };

  const updateProject = useUpdateProject({
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: getGetProjectQueryKey(projectId) });
      qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      qc.invalidateQueries({ queryKey: getProjectActivityQueryKey(projectId) });
    },
  });
  const { data: customers } = useListCustomers();
  const createCustomer = useCreateCustomer();
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [pendingCustomerName, setPendingCustomerName] = useState("");

  const assignCustomer = (customerId: number) => {
    setCustomerPickerOpen(false);
    setCustomerSearch("");
    if (customerId === project?.customerId) return;
    updateProject.mutate(
      { id: projectId, data: { customerId } },
      {
        onSuccess: () => {
          // The change order tab needs a customer; refetch it so it picks this one up.
          qc.invalidateQueries({ queryKey: getProjectCoEstimateQueryKey(projectId) });
          toast({ title: "Customer updated" });
        },
        onError: () => toast({ title: "Failed to update customer", variant: "destructive" }),
      },
    );
  };

  const handleCreateCustomer = (data: CreateCustomerFormValues) => {
    createCustomer.mutate(
      { data: { name: data.name, email: data.email || undefined, phone: data.phone || undefined, addressLine1: data.addressLine1 || undefined } },
      {
        onSuccess: (newCustomer) => {
          qc.invalidateQueries({ queryKey: getListCustomersQueryKey() });
          setCreateCustomerOpen(false);
          assignCustomer(newCustomer.id);
        },
        onError: () => toast({ title: "Failed to create customer", variant: "destructive" }),
      },
    );
  };

  const deleteProject = useDeleteProject({
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      navigate("/projects");
    },
  });

  if (isLoading || !project) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Loading…</div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-8">
      <Link href="/projects">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" />
          All Projects
        </Button>
      </Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{project.name}</h1>
          <div style={{ color: "hsl(var(--muted-foreground))", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.25rem" }}>
            <InlineCustomerSelect
              customerName={project.customer?.name ?? "Assign customer"}
              customerId={project.customerId ?? 0}
              customers={customers}
              open={customerPickerOpen}
              search={customerSearch}
              onOpenChange={setCustomerPickerOpen}
              onSearchChange={setCustomerSearch}
              onSelect={assignCustomer}
              onCreateNew={(name) => {
                setPendingCustomerName(name);
                setCreateCustomerOpen(true);
                setCustomerPickerOpen(false);
              }}
            />
            {project.customer && (
              <Link href={`/customers/${project.customer.id}`} className="hover:text-primary hover:underline text-xs">
                View profile
              </Link>
            )}
            <span aria-hidden="true">·</span>
            <InlineDateEditor
              label="Start date"
              value={project.startDate}
              otherDate={project.targetCompletionDate}
              isStart
              onSave={(next) => {
                const prevStart = project.startDate;
                qc.setQueryData(getGetProjectQueryKey(projectId), (old: ProjectDetail | undefined) =>
                  old ? { ...old, startDate: next } : old,
                );
                updateProject.mutate(
                  { id: projectId, data: { startDate: next } },
                  {
                    onError: (err) => {
                      qc.setQueryData(getGetProjectQueryKey(projectId), (old: ProjectDetail | undefined) =>
                        old ? { ...old, startDate: prevStart } : old,
                      );
                      toast({
                        title: "Failed to save start date",
                        description: err instanceof Error ? err.message : String(err),
                        variant: "destructive",
                      });
                    },
                  },
                );
              }}
            />
            <span aria-hidden="true">·</span>
            <InlineDateEditor
              label="Target completion"
              value={project.targetCompletionDate}
              otherDate={project.startDate}
              isStart={false}
              onSave={(next) => {
                const prevTarget = project.targetCompletionDate;
                qc.setQueryData(getGetProjectQueryKey(projectId), (old: ProjectDetail | undefined) =>
                  old ? { ...old, targetCompletionDate: next } : old,
                );
                updateProject.mutate(
                  { id: projectId, data: { targetCompletionDate: next } },
                  {
                    onError: (err) => {
                      qc.setQueryData(getGetProjectQueryKey(projectId), (old: ProjectDetail | undefined) =>
                        old ? { ...old, targetCompletionDate: prevTarget } : old,
                      );
                      toast({
                        title: "Failed to save target completion date",
                        description: err instanceof Error ? err.message : String(err),
                        variant: "destructive",
                      });
                    },
                  },
                );
              }}
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <Select
            value={project.status}
            onValueChange={(v) => updateProject.mutate({ id: projectId, data: { status: v as ProjectStatus } })}
          >
            <SelectTrigger style={{ width: 160 }}><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Link href={`/field/${projectId}`}>
            <Button size="sm">Field</Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting !== null}>
                {exporting !== null ? (
                  <Loader2 style={{ width: 16, height: 16, marginRight: 6 }} className="animate-spin" />
                ) : (
                  <Download style={{ width: 16, height: 16, marginRight: 6 }} />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={runExportPdf} disabled={exporting !== null}>
                <FileText style={{ width: 16, height: 16, marginRight: 8 }} />
                Summary PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={runExportZip} disabled={exporting !== null}>
                <Download style={{ width: 16, height: 16, marginRight: 8 }} />
                ZIP archive (PDF + quotes, invoices, files)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="icon"><Trash2 style={{ width: 16, height: 16 }} /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete project?</AlertDialogTitle>
                <AlertDialogDescription>
                  This deletes the project, its actual costs, files and activity. Quotes and invoices will be detached but kept.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteProject.mutate(projectId)}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <hr className="border-border" />

      <Tabs value={activeProjectTab} onValueChange={setActiveProjectTab}>
        <TabsList className="h-auto w-full gap-1 p-1.5">
          <TabsTrigger value="overview" className="flex-1 justify-center gap-2 px-4 py-2 text-base" title="Overview">
            <LayoutDashboard className="h-5 w-5" />
            {!isMobile && "Overview"}
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex-1 justify-center gap-2 px-4 py-2 text-base" title="Billing">
            <Receipt className="h-5 w-5" />
            {!isMobile && "Billing"}
          </TabsTrigger>
          <TabsTrigger value="costs" className="flex-1 justify-center gap-2 px-4 py-2 text-base" data-tour-target="project-costs-tab" title="Costs">
            <DollarSign className="h-5 w-5" />
            {!isMobile && "Costs"}
          </TabsTrigger>
          <TabsTrigger value="changeorders" className="flex-1 justify-center gap-2 px-4 py-2 text-base" title="Change Orders">
            <ClipboardList className="h-5 w-5" />
            {!isMobile && "Change Orders"}
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex-1 justify-center gap-2 px-4 py-2 text-base" title="Notes & Files">
            <StickyNote className="h-5 w-5" />
            {!isMobile && "Notes & Files"}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab projectId={projectId} agg={agg} project={project} />
        </TabsContent>
        <TabsContent value="billing">
          <BillingTab projectId={projectId} customerId={project?.customerId ?? null} />
        </TabsContent>
        <TabsContent value="costs">
          <CostsTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="changeorders">
          <ChangeOrdersTab projectId={projectId} projectName={project?.name ?? ""} customerId={project?.customerId ?? null} />
        </TabsContent>
        <TabsContent value="notes">
          <NotesFilesTab projectId={projectId} isPro={isPro} initialNotes={project.notes ?? ""} />
        </TabsContent>
      </Tabs>

      <CreateCustomerModal
        open={createCustomerOpen}
        initialName={pendingCustomerName}
        isSubmitting={createCustomer.isPending}
        onOpenChange={setCreateCustomerOpen}
        onSubmit={handleCreateCustomer}
      />
    </div>
  );
}

function StatGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
      <div
        style={{
          fontSize: "0.75rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "hsl(var(--muted-foreground))",
        }}
      >
        {label}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem" }}>
        {children}
      </div>
    </div>
  );
}

function StatBox({ label, value, color, tip }: { label: string; value: string; color?: string; tip?: string }) {
  return (
    <Card style={{ borderTop: `3px solid ${color || "hsl(var(--border))"}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      <CardContent style={{ padding: "1rem 1.25rem" }}>
        <div style={{ fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "hsl(var(--muted-foreground))", marginBottom: "0.375rem" }} className="flex items-center gap-1">
          {label}{tip && <InfoTip content={tip} />}
        </div>
        <div style={{ fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.2, color: color || "hsl(var(--foreground))" }}>{value}</div>
      </CardContent>
    </Card>
  );
}

function OverviewTab({ projectId, agg, project }: {
  projectId: number;
  agg: ReturnType<typeof useProjectAggregates>["data"];
  project: ProjectDetail | undefined;
}) {
  const { formatCurrency } = useSettingsFormat();
  const [activityOpen, setActivityOpen] = useState(false);
  // Margin colours follow the target margin in Settings.
  const { tone } = useTargetMargin();
  const marginColor = (margin: number) => TONE_HEX[tone(margin)];
  if (!agg) return <p style={{ padding: "1rem" }}>Loading…</p>;
  const customer = project?.customer;

  const categories = COST_TYPES;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
      {customer && (
        <Card>
          <CardContent style={{ padding: "1rem", display: "flex", flexWrap: "wrap", gap: "1.5rem", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Customer</div>
              <Link href={`/customers/${customer.id}`} style={{ fontWeight: 600, fontSize: "1rem" }}>{customer.name}</Link>
            </div>
            {customer.email && (
              <div>
                <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Email</div>
                <div>{customer.email}</div>
              </div>
            )}
            {customer.phone && (
              <div>
                <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Phone</div>
                <div>{customer.phone}</div>
              </div>
            )}
            {customer.address && (
              <div>
                <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Address</div>
                <div>{customer.address}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <StatGroup label="Revenue">
        <StatBox label="Quoted" value={formatCurrency(agg.quotedTotal)} color="#3b82f6" tip={FORMULAS.projectQuoted} />
        <StatBox label="Invoiced" value={formatCurrency(agg.invoicedTotal)} color="#6366f1" tip={FORMULAS.projectInvoiced} />
        <StatBox label="Paid" value={formatCurrency(agg.paidTotal)} color="#22c55e" tip={FORMULAS.projectPaid} />
        <StatBox label="Outstanding" value={formatCurrency(agg.outstanding)} color="#f59e0b" tip={FORMULAS.projectOutstanding} />
      </StatGroup>
      <div data-tour-target="project-quoted-economics" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <StatGroup label="Quoted economics">
          <StatBox label="Quoted Cost" value={formatCurrency(agg.quotedInternalCost)} color="#64748b" tip={FORMULAS.projectQuotedCost} />
          <StatBox label="Quoted Profit" value={formatCurrency(agg.projectedProfit)} color={marginColor(agg.quotedMargin)} tip={FORMULAS.projectQuotedProfit} />
          <StatBox label="Quoted Margin" value={`${agg.quotedMargin.toFixed(1)}%`} color={marginColor(agg.quotedMargin)} tip={FORMULAS.projectQuotedMargin} />
        </StatGroup>
        <StatGroup label="Actual economics">
          <StatBox label="Actual Cost" value={formatCurrency(agg.actualTotal)} color="#64748b" tip={FORMULAS.projectActualCost} />
          <StatBox label="Actual Profit" value={formatCurrency(agg.actualProfit)} color={marginColor(agg.margin)} tip={FORMULAS.projectActualProfit} />
          <StatBox label="Actual Margin" value={`${agg.margin.toFixed(1)}%`} color={marginColor(agg.margin)} tip={FORMULAS.projectActualMargin} />
        </StatGroup>
      </div>

      <Card data-tour-target="project-cost-chart">
        <CardHeader><CardTitle style={{ fontSize: "1rem" }}>Quoted vs actual by category</CardTitle></CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 260, marginBottom: "1rem" }}>
            <ResponsiveContainer>
              <BarChart data={categories.map(cat => ({
                category: COST_TAB_LABELS[cat],
                Quoted: agg.quotedByCategory[cat] ?? 0,
                Actual: agg.actualByCategory[cat] ?? 0,
              }))}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="category" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={formatAxisMoney} />
                <Tooltip
                  formatter={(v: number) => formatCurrency(v)}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}
                />
                <Legend />
                <Bar dataKey="Quoted" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Actual" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <table style={{ width: "100%", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "hsl(var(--muted-foreground))" }}>
                <th style={{ padding: "0.5rem 0" }}>Category</th>
                <th style={{ padding: "0.5rem 0", textAlign: "right" }}>Quoted</th>
                <th style={{ padding: "0.5rem 0", textAlign: "right" }}>Actual</th>
                <th style={{ padding: "0.5rem 0", textAlign: "right" }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => {
                const q = agg.quotedByCategory[cat] ?? 0;
                const a = agg.actualByCategory[cat] ?? 0;
                const v = q - a;
                return (
                  <tr key={cat} style={{ borderTop: "1px solid hsl(var(--border))" }}>
                    <td style={{ padding: "0.5rem 0" }}>{COST_TAB_LABELS[cat]}</td>
                    <td style={{ padding: "0.5rem 0", textAlign: "right" }}>{formatCurrency(q)}</td>
                    <td style={{ padding: "0.5rem 0", textAlign: "right" }}>{formatCurrency(a)}</td>
                    <td style={{ padding: "0.5rem 0", textAlign: "right", color: v >= 0 ? "#10b981" : "#ef4444" }}>
                      {v >= 0 ? "+" : ""}{formatCurrency(v)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: "2px solid hsl(var(--border))", fontWeight: 700 }}>
                <td style={{ padding: "0.5rem 0" }}>Total</td>
                <td style={{ padding: "0.5rem 0", textAlign: "right" }}>{formatCurrency(agg.quotedInternalCost)}</td>
                <td style={{ padding: "0.5rem 0", textAlign: "right" }}>{formatCurrency(agg.actualTotal)}</td>
                <td style={{ padding: "0.5rem 0", textAlign: "right", color: agg.variance >= 0 ? "#10b981" : "#ef4444" }}>
                  {agg.variance >= 0 ? "+" : ""}{formatCurrency(agg.variance)} ({agg.variancePct.toFixed(1)}%)
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {agg.lineItems && agg.lineItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: "1rem" }}>Line item comparison</CardTitle>
            <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginTop: 4 }}>
              Estimated line items joined with their actual costs (matched by source or name).
            </p>
          </CardHeader>
          <CardContent>
            <table style={{ width: "100%", fontSize: "0.875rem" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "hsl(var(--muted-foreground))" }}>
                  <th style={{ padding: "0.5rem 0" }}>Item</th>
                  <th style={{ padding: "0.5rem 0", textTransform: "capitalize" }}>Type</th>
                  <th style={{ padding: "0.5rem 0", textAlign: "right" }}>Quoted</th>
                  <th style={{ padding: "0.5rem 0", textAlign: "right" }}>Actual</th>
                  <th style={{ padding: "0.5rem 0", textAlign: "right" }}>Variance</th>
                </tr>
              </thead>
              <tbody>
                {agg.lineItems.map((li, i) => (
                  <tr key={`${li.type}-${li.name}-${i}`} style={{ borderTop: "1px solid hsl(var(--border))" }}>
                    <td style={{ padding: "0.5rem 0" }}>{li.name}</td>
                    <td style={{ padding: "0.5rem 0", textTransform: "capitalize", color: "hsl(var(--muted-foreground))" }}>{COST_TAB_LABELS[li.type as CostTabKey] ?? li.type}</td>
                    <td style={{ padding: "0.5rem 0", textAlign: "right" }}>{formatCurrency(li.quoted)}</td>
                    <td style={{ padding: "0.5rem 0", textAlign: "right" }}>{formatCurrency(li.actual)}</td>
                    <td style={{ padding: "0.5rem 0", textAlign: "right", color: li.variance >= 0 ? "#10b981" : "#ef4444" }}>
                      {li.variance >= 0 ? "+" : ""}{formatCurrency(li.variance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {agg.pendingCoCount > 0 && (
        <Card style={{ borderLeft: "3px solid #f59e0b" }}>
          <CardContent style={{ padding: "0.875rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.9375rem" }}>
                {agg.pendingCoCount} pending change order {agg.pendingCoCount === 1 ? "item" : "items"}
              </div>
              <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
                {formatCurrency(agg.pendingCoTotal)} additional revenue · {formatCurrency(agg.pendingCoInternalCost)} additional cost
              </div>
            </div>
            <Link href={`/projects/${projectId}?tab=changeorders`}>
              <Button variant="outline" size="sm">View Change Orders</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Collapsible activity section */}
      <div>
        <button
          onClick={() => setActivityOpen(o => !o)}
          style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            background: "none", border: "none", cursor: "pointer",
            color: "hsl(var(--muted-foreground))", fontSize: "0.875rem",
            padding: "0.5rem 0", width: "100%",
          }}
        >
          {activityOpen ? <ChevronDown style={{ width: 16, height: 16 }} /> : <ChevronRight style={{ width: 16, height: 16 }} />}
          Project Activity
        </button>
        {activityOpen && <ActivityTab projectId={projectId} />}
      </div>
    </div>
  );
}

type CostTabKey = "all" | CatalogCostType;
type RowStatus = "idle" | "saving" | "saved" | "error";

const COST_TAB_LABELS: Record<CostTabKey, string> = {
  all: "All",
  labour: "Labor",
  materials: "Materials",
  fees: "Fees",
};

function legacyTypeLabel(t: string) {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const cellStyle: CSSProperties = { padding: "0.375rem 0.5rem", verticalAlign: "middle" };
const cellInputStyle: CSSProperties = { height: 32, fontSize: "0.875rem" };

function CostsTab({ projectId }: { projectId: number }) {
  const { data: costs } = useProjectActualCosts(projectId);
  const [activeTab, setActiveTab] = useState<CostTabKey>("all");
  const [draftSeq, setDraftSeq] = useState(0);
  const [draft, setDraft] = useState<{ key: number; type: ProjectCostType } | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const counts: Record<CostTabKey, number> = {
    all: costs?.length ?? 0,
    labour: 0, materials: 0, fees: 0,
  };
  for (const c of costs ?? []) {
    if (CATALOG_COST_TYPE_SET.has(c.type)) {
      counts[c.type as CatalogCostType] += 1;
    }
  }

  const handleAdd = () => {
    const type: CatalogCostType = activeTab === "all" ? "materials" : activeTab;
    setDraftSeq(s => s + 1);
    setDraft({ key: draftSeq + 1, type });
  };

  return (
    <div style={{ marginTop: "1rem" }}>
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as CostTabKey); setDraft(null); }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
          <TabsList>
            {(["all", ...COST_TYPES] as CostTabKey[]).map(k => (
              <TabsTrigger key={k} value={k} style={{ textTransform: "capitalize" }}>
                {COST_TAB_LABELS[k]}
                <span style={{
                  marginLeft: 6, fontSize: "0.75rem",
                  background: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))",
                  borderRadius: 999, padding: "0 6px", minWidth: 18, display: "inline-block", textAlign: "center",
                }}>{counts[k]}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button size="sm" variant="outline" onClick={() => setReceiptOpen(o => !o)}>
              <Receipt style={{ width: 14, height: 14, marginRight: 4 }} />
              {receiptOpen ? "Cancel import" : "Import receipt"}
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={!!draft} data-tour-target="project-add-cost-btn">
              <Plus style={{ width: 14, height: 14, marginRight: 4 }} /> Add cost
            </Button>
          </div>
        </div>

        {receiptOpen && (
          <Card style={{ marginBottom: "0.75rem" }}>
            <CardContent style={{ padding: "1rem" }}>
              <p style={{ fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Receipt style={{ width: 14, height: 14 }} /> Import from receipt
              </p>
              <ReceiptImport projectId={projectId} />
            </CardContent>
          </Card>
        )}

        {(["all", ...COST_TYPES] as CostTabKey[]).map(k => {
          const tabCosts = (costs ?? []).filter(c => k === "all" || c.type === k);
          const showDraftHere = !!draft && (k === "all" || k === draft.type);
          return (
            <TabsContent key={k} value={k}>
              {tabCosts.length === 0 && !showDraftHere ? (
                <p style={{ padding: "1rem", color: "hsl(var(--muted-foreground))" }}>
                  {k === "all"
                    ? "No costs yet. Attach a quote to seed materials, or add costs manually."
                    : `No ${COST_TAB_LABELS[k].toLowerCase()} costs yet.`}
                </p>
              ) : (
                <Card>
                  <CardContent style={{ padding: 0, overflowX: "auto" }}>
                    <table style={{ width: "100%", minWidth: "1050px", fontSize: "0.875rem", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ textAlign: "left", borderBottom: "1px solid hsl(var(--border))" }}>
                          <th style={{ padding: "0.5rem", minWidth: 180 }}>Name</th>
                          <th style={{ padding: "0.5rem", width: 150 }}>Type</th>
                          <th style={{ padding: "0.5rem", minWidth: 140 }}>Vendor</th>
                          <th style={{ padding: "0.5rem", width: 90, textAlign: "right" }}>Qty</th>
                          <th style={{ padding: "0.5rem", width: 110, textAlign: "right" }}>Quoted</th>
                          <th style={{ padding: "0.5rem", width: 120, textAlign: "right" }}>Actual</th>
                          <th style={{ padding: "0.5rem", width: 150 }}>Date</th>
                          <th style={{ padding: "0.5rem", width: 120, textAlign: "right" }}>Amount</th>
                          <th style={{ padding: "0.5rem", width: 90 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {showDraftHere && draft && (
                          <DraftCostRow
                            key={`draft-${draft.key}`}
                            projectId={projectId}
                            initialType={draft.type}
                            onDone={() => setDraft(null)}
                          />
                        )}
                        {tabCosts.map(c => (
                          <CostRow key={c.id} projectId={projectId} cost={c} />
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

function StatusIndicator({ status }: { status: RowStatus }) {
  if (status === "idle") return null;
  const text = status === "saving" ? "Saving…" : status === "saved" ? "Saved" : "Error";
  const color = status === "error" ? "#ef4444" : status === "saved" ? "#10b981" : "hsl(var(--muted-foreground))";
  return <span style={{ fontSize: "0.75rem", color, marginLeft: 6 }}>{text}</span>;
}

function CostRow({ projectId, cost }: { projectId: number; cost: ProjectActualCost }) {
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const qc = useQueryClient();
  const update = useUpdateProjectActualCost();
  const del = useDeleteProjectActualCost();

  const [name, setName] = useState(cost.name);
  const [type, setType] = useState<ProjectCostType>(cost.type);
  const [vendor, setVendor] = useState(cost.vendor ?? "");
  const [quantity, setQuantity] = useState(String(cost.quantity));
  const [unitCost, setUnitCost] = useState(String(cost.unitCost));
  const [date, setDate] = useState(cost.date ?? "");
  const [status, setStatus] = useState<RowStatus>("idle");

  useEffect(() => {
    setName(cost.name);
    setType(cost.type);
    setVendor(cost.vendor ?? "");
    setQuantity(String(cost.quantity));
    setUnitCost(String(cost.unitCost));
    setDate(cost.date ?? "");
  }, [cost.id, cost.updatedAt]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getProjectActualCostsQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectAggregatesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectActivityQueryKey(projectId) });
  };

  const revert = () => {
    setName(cost.name);
    setType(cost.type);
    setVendor(cost.vendor ?? "");
    setQuantity(String(cost.quantity));
    setUnitCost(String(cost.unitCost));
    setDate(cost.date ?? "");
  };

  const pendingRef = useRef<Partial<CreateProjectActualCostBody>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    const data = pendingRef.current;
    if (!data || Object.keys(data).length === 0) return;
    pendingRef.current = {};
    setStatus("saving");
    update.mutate({ id: projectId, costId: cost.id, data }, {
      onSuccess: () => {
        setStatus("saved");
        invalidate();
        setTimeout(() => setStatus(s => (s === "saved" ? "idle" : s)), 1200);
      },
      onError: (err) => {
        setStatus("error");
        revert();
        toast({ title: "Save failed", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
        setTimeout(() => setStatus(s => (s === "error" ? "idle" : s)), 1500);
      },
    });
  };

  const save = (data: Partial<CreateProjectActualCostBody>) => {
    pendingRef.current = { ...pendingRef.current, ...data };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flush, 400);
  };

  useEffect(() => () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); flush(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commitName = () => {
    const v = name.trim();
    if (!v) { setName(cost.name); return; }
    if (v !== cost.name) save({ name: v });
  };
  const commitVendor = () => {
    const v = vendor.trim();
    const norm = v || null;
    if (norm !== (cost.vendor ?? null)) save({ vendor: norm });
  };
  const commitQuantity = () => {
    const n = Number(quantity);
    if (!Number.isFinite(n) || n < 0) { setQuantity(String(cost.quantity)); return; }
    if (n !== cost.quantity) save({ quantity: n });
  };
  const commitUnitCost = () => {
    const n = Number(unitCost);
    if (!Number.isFinite(n) || n < 0) { setUnitCost(String(cost.unitCost)); return; }
    if (n !== cost.unitCost) save({ unitCost: n });
  };
  const commitDate = () => {
    const v = date || null;
    if (v !== (cost.date ?? null)) save({ date: v });
  };
  const commitType = (v: ProjectCostType) => {
    setType(v);
    if (v !== cost.type) save({ type: v });
  };

  const blurOnEnter = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); }
  };

  const liveAmount = (Number(quantity) || 0) * (Number(unitCost) || 0);
  // Quote labour the crew's time log has replaced: kept for comparison, not counted.
  const replaced = !!cost.replacedByTimeLog;

  return (
    <tr style={{ borderBottom: "1px solid hsl(var(--border))", opacity: replaced ? 0.6 : 1 }}>
      <td style={cellStyle}>
        <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={commitName} onKeyDown={blurOnEnter} style={cellInputStyle} />
        <StatusIndicator status={status} />
        {replaced && (
          <span style={{ display: "block", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
            Replaced by time log. Not counted in actual cost.
          </span>
        )}
      </td>
      <td style={cellStyle}>
        <Select value={type} onValueChange={(v) => commitType(v as ProjectCostType)}>
          <SelectTrigger style={cellInputStyle}><SelectValue /></SelectTrigger>
          <SelectContent>
            {COST_TYPES.map(t => <SelectItem key={t} value={t} style={{ textTransform: "capitalize" }}>{COST_TAB_LABELS[t]}</SelectItem>)}
            {!CATALOG_COST_TYPE_SET.has(type) && (
              <SelectItem key={type} value={type} style={{ textTransform: "capitalize" }}>
                {legacyTypeLabel(type)}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </td>
      <td style={cellStyle}>
        <Input value={vendor} onChange={(e) => setVendor(e.target.value)} onBlur={commitVendor} onKeyDown={blurOnEnter} style={cellInputStyle} />
      </td>
      <td style={cellStyle}>
        <Input type="number" step="0.01" value={quantity} onChange={(e) => setQuantity(e.target.value)} onBlur={commitQuantity} onKeyDown={blurOnEnter} style={{ ...cellInputStyle, textAlign: "right" }} />
      </td>
      <td style={{ ...cellStyle, textAlign: "right", color: "hsl(var(--muted-foreground))" }}>
        {cost.quotedUnitCost != null ? formatCurrency(cost.quotedUnitCost) : <span style={{ opacity: 0.4 }}>—</span>}
      </td>
      <td style={cellStyle}>
        <Input type="number" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} onBlur={commitUnitCost} onKeyDown={blurOnEnter} style={{ ...cellInputStyle, textAlign: "right" }} />
      </td>
      <td style={cellStyle}>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={commitDate} onKeyDown={blurOnEnter} style={cellInputStyle} />
      </td>
      <td style={{ ...cellStyle, textAlign: "right", fontWeight: 600, textDecoration: replaced ? "line-through" : undefined }}>{formatCurrency(liveAmount)}</td>
      <td style={cellStyle}>
        <Button variant="ghost" size="icon" onClick={() => {
          del.mutate({ id: projectId, costId: cost.id }, {
            onSuccess: () => { toast({ title: "Deleted" }); invalidate(); },
          });
        }}><Trash2 style={{ width: 14, height: 14 }} /></Button>
      </td>
    </tr>
  );
}

function DraftCostRow({ projectId, initialType, onDone }: {
  projectId: number;
  initialType: ProjectCostType;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const qc = useQueryClient();
  const create = useCreateProjectActualCost();

  const [name, setName] = useState("");
  const [type, setType] = useState<ProjectCostType>(initialType);
  const [vendor, setVendor] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitCost, setUnitCost] = useState("0");
  const [date, setDate] = useState("");
  const [status, setStatus] = useState<RowStatus>("idle");
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getProjectActualCostsQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectAggregatesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectActivityQueryKey(projectId) });
  };

  const trySave = () => {
    const n = name.trim();
    if (!n) return;
    setStatus("saving");
    create.mutate({
      id: projectId,
      data: {
        name: n,
        type,
        vendor: vendor.trim() || null,
        quantity: Number(quantity) || 0,
        unitCost: Number(unitCost) || 0,
        date: date || null,
      },
    }, {
      onSuccess: () => { setStatus("saved"); toast({ title: "Cost added" }); invalidate(); onDone(); emitTourEvent("project.cost-added"); },
      onError: (err) => {
        setStatus("error");
        toast({ title: "Save failed", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
      },
    });
  };

  const blurOnEnter = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); }
  };

  const liveAmount = (Number(quantity) || 0) * (Number(unitCost) || 0);

  return (
    <tr style={{ borderBottom: "1px solid hsl(var(--border))", background: "hsl(var(--muted) / 0.3)" }}>
      <td style={cellStyle}>
        <Input ref={nameRef} value={name} placeholder="New cost name" onChange={(e) => setName(e.target.value)} onBlur={trySave} onKeyDown={blurOnEnter} style={cellInputStyle} />
        <StatusIndicator status={status} />
      </td>
      <td style={cellStyle}>
        <Select value={type} onValueChange={(v) => setType(v as ProjectCostType)}>
          <SelectTrigger style={cellInputStyle}><SelectValue /></SelectTrigger>
          <SelectContent>
            {COST_TYPES.map(t => <SelectItem key={t} value={t} style={{ textTransform: "capitalize" }}>{COST_TAB_LABELS[t]}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td style={cellStyle}>
        <Input value={vendor} onChange={(e) => setVendor(e.target.value)} onBlur={trySave} onKeyDown={blurOnEnter} style={cellInputStyle} />
      </td>
      <td style={cellStyle}>
        <Input type="number" step="0.01" value={quantity} onChange={(e) => setQuantity(e.target.value)} onBlur={trySave} onKeyDown={blurOnEnter} style={{ ...cellInputStyle, textAlign: "right" }} />
      </td>
      <td style={{ ...cellStyle, textAlign: "right", color: "hsl(var(--muted-foreground))" }}>
        <span style={{ opacity: 0.4 }}>—</span>
      </td>
      <td style={cellStyle}>
        <Input type="number" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} onBlur={trySave} onKeyDown={blurOnEnter} style={{ ...cellInputStyle, textAlign: "right" }} />
      </td>
      <td style={cellStyle}>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={trySave} onKeyDown={blurOnEnter} style={cellInputStyle} />
      </td>
      <td style={{ ...cellStyle, textAlign: "right", fontWeight: 600 }}>{formatCurrency(liveAmount)}</td>
      <td style={cellStyle}>
        <Button variant="ghost" size="icon" title="Cancel" onMouseDown={(e) => e.preventDefault()} onClick={onDone}>
          <X style={{ width: 14, height: 14 }} />
        </Button>
      </td>
    </tr>
  );
}


function NotesFilesTab({ projectId, isPro, initialNotes }: { projectId: number; isPro: boolean; initialNotes: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const update = useUpdateProject({
    onSuccess: () => {
      toast({ title: "Notes saved" });
      qc.invalidateQueries({ queryKey: getGetProjectQueryKey(projectId) });
    },
  });

  const editor = useEditor({
    extensions: [StarterKit],
    content: initialNotes || "",
    editorProps: {
      attributes: {
        style: "min-height: 220px; outline: none; padding: 1rem; font-size: 0.9375rem; line-height: 1.7;",
      },
    },
  });

  const saveNotes = () => {
    const html = editor?.getHTML() ?? "";
    update.mutate({ id: projectId, data: { notes: html } });
  };

  // Files state
  const { data: files } = useProjectFiles(projectId);
  const create = useCreateProjectFile();
  const del = useDeleteProjectFile();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const invalidateFiles = () => {
    qc.invalidateQueries({ queryKey: getProjectFilesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectActivityQueryKey(projectId) });
  };

  const handleFile = async (file: File) => {
    if (!isPro) { toast({ title: "Pro plan required", variant: "destructive" }); return; }
    setUploading(true);
    try {
      const { uploadURL, objectPath } = await requestUploadUrl(file);
      await uploadToGcs(file, uploadURL);
      await new Promise<void>((resolve, reject) => {
        create.mutate({ id: projectId, data: { fileName: file.name, contentType: file.type || "application/octet-stream", objectPath } }, {
          onSuccess: () => { toast({ title: "Uploaded" }); invalidateFiles(); resolve(); },
          onError: (err) => { toast({ title: "Upload failed", description: String(err), variant: "destructive" }); reject(err); },
        });
      });
    } catch (err) {
      toast({ title: "Upload failed", description: err instanceof Error ? err.message : String(err), variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Notes section */}
      <div>
        <div style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.5rem", color: "hsl(var(--foreground))" }}>Notes</div>
        <Card>
          {/* Toolbar */}
          <div style={{ display: "flex", gap: "0.25rem", padding: "0.5rem", borderBottom: "1px solid hsl(var(--border))", flexWrap: "wrap" }}>
            {[
              { label: <Bold style={{ width: 14, height: 14 }} />, title: "Bold", action: () => editor?.chain().focus().toggleBold().run(), active: () => editor?.isActive("bold") },
              { label: <Italic style={{ width: 14, height: 14 }} />, title: "Italic", action: () => editor?.chain().focus().toggleItalic().run(), active: () => editor?.isActive("italic") },
              { label: <Heading2 style={{ width: 14, height: 14 }} />, title: "Heading", action: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(), active: () => editor?.isActive("heading", { level: 2 }) },
              { label: <List style={{ width: 14, height: 14 }} />, title: "Bullet list", action: () => editor?.chain().focus().toggleBulletList().run(), active: () => editor?.isActive("bulletList") },
              { label: <ListOrdered style={{ width: 14, height: 14 }} />, title: "Numbered list", action: () => editor?.chain().focus().toggleOrderedList().run(), active: () => editor?.isActive("orderedList") },
            ].map((btn, i) => (
              <Button
                key={i}
                variant={btn.active?.() ? "secondary" : "ghost"}
                size="icon"
                title={btn.title}
                style={{ width: 28, height: 28 }}
                onMouseDown={(e) => { e.preventDefault(); btn.action(); }}
              >
                {btn.label}
              </Button>
            ))}
          </div>
          <EditorContent editor={editor} />
        </Card>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.5rem" }}>
          <Button onClick={saveNotes} disabled={update.isPending} size="sm">
            {update.isPending ? "Saving…" : "Save notes"}
          </Button>
        </div>
      </div>

      {/* Files section */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
          <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "hsl(var(--foreground))" }}>Files</div>
          <input ref={fileInput} type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          <Button size="sm" disabled={uploading || !isPro} onClick={() => fileInput.current?.click()}>
            <Upload style={{ width: 14, height: 14, marginRight: 4 }} /> {uploading ? "Uploading…" : "Upload file"}
          </Button>
        </div>
        {!isPro && (
          <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>File attachments are a Pro feature.</p>
        )}
        {!files || files.length === 0 ? (
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>No files yet.</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.75rem" }}>
            {files.map(f => {
              const isImg = f.contentType.startsWith("image/");
              const url = `/api/storage${f.objectPath}`;
              return (
                <Card key={f.id}>
                  <CardContent style={{ padding: "0.75rem" }}>
                    <a href={url} target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none", color: "inherit" }}>
                      {isImg ? (
                        <img src={url} alt={f.fileName} style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", borderRadius: "0.375rem", marginBottom: "0.5rem" }} />
                      ) : (
                        <div style={{ width: "100%", aspectRatio: "1/1", display: "flex", alignItems: "center", justifyContent: "center", background: "hsl(var(--muted))", borderRadius: "0.375rem", marginBottom: "0.5rem" }}>
                          <FileText style={{ width: 40, height: 40, color: "hsl(var(--muted-foreground))" }} />
                        </div>
                      )}
                      <div style={{ fontSize: "0.8125rem", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.fileName}</div>
                    </a>
                    <Button variant="ghost" size="sm" style={{ marginTop: 4 }} onClick={() => {
                      del.mutate({ id: projectId, fileId: f.id }, { onSuccess: () => { toast({ title: "Deleted" }); invalidateFiles(); } });
                    }}><X style={{ width: 12, height: 12, marginRight: 4 }} /> Delete</Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BillingTab — merged Quotes + Invoices
// ---------------------------------------------------------------------------

function BillingTab({ projectId, customerId }: { projectId: number; customerId: number | null }) {
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const qc = useQueryClient();

  const { data: quotes } = useProjectQuotes(projectId);
  const { data: invoices } = useProjectInvoices(projectId);
  const { data: allQuotes } = useListEstimates();
  const { data: allInvoices } = useListInvoices();

  const [showAttachQuote, setShowAttachQuote] = useState(false);
  const [showAttachInvoice, setShowAttachInvoice] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState("");

  const attachQuote = useAttachQuote();
  const detachQuote = useDetachQuote();
  const resync = useResyncProjectFromQuotes();
  const attachInvoice = useAttachInvoice();
  const detachInvoice = useDetachInvoice();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getProjectQuotesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectInvoicesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectAggregatesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectActualCostsQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectActivityQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getGetProjectQueryKey(projectId) });
  };

  const attachedQuoteIds = new Set((quotes ?? []).map(q => q.id));
  const unattachedQuotes = (allQuotes ?? []).filter(q => !attachedQuoteIds.has(q.id));
  const attachedInvoiceIds = new Set((invoices ?? []).map(i => i.id));
  const unattachedInvoices = (allInvoices ?? []).filter(i => !attachedInvoiceIds.has(i.id));

  const { topLevel, childrenByParent } = (() => {
    const all = invoices ?? [];
    const byParent = new Map<number, typeof all>();
    const top: typeof all = [];
    for (const inv of all) {
      const parentId = inv.depositParentId ?? inv.milestoneParentId ?? null;
      if (parentId !== null) {
        const existing = byParent.get(parentId) ?? [];
        existing.push(inv);
        byParent.set(parentId, existing);
      } else {
        top.push(inv);
      }
    }
    return { topLevel: top, childrenByParent: byParent };
  })();

  function childTypeLabel(inv: { isDepositChild: boolean; milestoneParentId: number | null }) {
    if (inv.isDepositChild) return "Deposit";
    if (inv.milestoneParentId != null) return "Milestone";
    return null;
  }

  return (
    <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Quotes section */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", gap: "0.5rem", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 600, fontSize: "0.9375rem" }}>Quotes</div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Button variant="outline" size="sm" onClick={() => {
              resync.mutate(projectId, {
                onSuccess: (r) => { toast({ title: `Re-seeded ${r.added} cost rows from quotes` }); invalidate(); },
              });
            }}>
              <RefreshCw style={{ width: 14, height: 14, marginRight: 4 }} /> Re-seed actuals
            </Button>
            <Link href={`/quotes/new?project=${projectId}${customerId ? `&customer=${customerId}` : ""}`}>
              <Button size="sm" variant="outline">
                <Plus style={{ width: 14, height: 14, marginRight: 4 }} /> New quote
              </Button>
            </Link>
            <Dialog open={showAttachQuote} onOpenChange={setShowAttachQuote}>
              <Button size="sm" onClick={() => setShowAttachQuote(true)}>
                <Link2 style={{ width: 14, height: 14, marginRight: 4 }} /> Attach quote
              </Button>
              <DialogContent>
                <DialogHeader><DialogTitle>Attach existing quote</DialogTitle></DialogHeader>
                <Select value={selectedQuote} onValueChange={setSelectedQuote}>
                  <SelectTrigger><SelectValue placeholder="Select a quote..." /></SelectTrigger>
                  <SelectContent>
                    {unattachedQuotes.map(q => (
                      <SelectItem key={q.id} value={String(q.id)}>
                        {q.estimateNumber ?? `#${q.id}`} — {q.title} ({q.customerName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAttachQuote(false)}>Cancel</Button>
                  <Button disabled={!selectedQuote || attachQuote.isPending} onClick={() => {
                    attachQuote.mutate({ projectId, quoteId: Number(selectedQuote) }, {
                      onSuccess: (r) => {
                        toast({ title: `Attached. Seeded ${r.seededCosts} cost rows.` });
                        invalidate(); setShowAttachQuote(false); setSelectedQuote("");
                      },
                    });
                  }}>Attach</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        {!quotes || quotes.length === 0 ? (
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>No quotes attached.</p>
        ) : (
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {quotes.map(q => (
              <Card key={q.id}>
                <CardContent style={{ padding: "0.75rem 1rem", display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: "1rem", alignItems: "center" }}>
                  <Link href={`/quotes/${q.id}`}>
                    <div style={{ cursor: "pointer" }}>
                      <div style={{ fontWeight: 600 }}>{q.estimateNumber ?? `#${q.id}`} — {q.title}</div>
                      <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>{q.customerName}</div>
                    </div>
                  </Link>
                  <div style={{ fontSize: "0.875rem", textTransform: "capitalize" }}>{q.status}</div>
                  <div style={{ fontSize: "0.875rem" }}>{q.estimateDate ?? "—"}</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(q.total)}</div>
                  <Button variant="ghost" size="icon" onClick={() => {
                    detachQuote.mutate({ projectId, quoteId: q.id }, { onSuccess: () => { toast({ title: "Detached" }); invalidate(); } });
                  }}><Unlink style={{ width: 14, height: 14 }} /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <hr className="border-border" />

      {/* Invoices section */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", gap: "0.5rem", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 600, fontSize: "0.9375rem" }}>Invoices</div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Link href={`/invoices/new?project=${projectId}${customerId ? `&customer=${customerId}` : ""}`}>
              <Button size="sm" variant="outline">
                <Plus style={{ width: 14, height: 14, marginRight: 4 }} /> New invoice
              </Button>
            </Link>
            <Dialog open={showAttachInvoice} onOpenChange={setShowAttachInvoice}>
              <Button size="sm" onClick={() => setShowAttachInvoice(true)}>
                <Link2 style={{ width: 14, height: 14, marginRight: 4 }} /> Attach invoice
              </Button>
              <DialogContent>
                <DialogHeader><DialogTitle>Attach existing invoice</DialogTitle></DialogHeader>
                <Select value={selectedInvoice} onValueChange={setSelectedInvoice}>
                  <SelectTrigger><SelectValue placeholder="Select an invoice..." /></SelectTrigger>
                  <SelectContent>
                    {unattachedInvoices.map(i => (
                      <SelectItem key={i.id} value={String(i.id)}>
                        {i.invoiceNumber ?? `#${i.id}`} — {i.title} ({i.customerName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAttachInvoice(false)}>Cancel</Button>
                  <Button disabled={!selectedInvoice || attachInvoice.isPending} onClick={() => {
                    attachInvoice.mutate({ projectId, invoiceId: Number(selectedInvoice) }, {
                      onSuccess: () => { toast({ title: "Invoice attached" }); invalidate(); setShowAttachInvoice(false); setSelectedInvoice(""); },
                    });
                  }}>Attach</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        {!invoices || invoices.length === 0 ? (
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>No invoices attached.</p>
        ) : (
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {topLevel.map(i => {
              const children = childrenByParent.get(i.id) ?? [];
              return (
                <div key={i.id}>
                  <Card>
                    <CardContent style={{ padding: "0.75rem 1rem", display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto", gap: "1rem", alignItems: "center" }}>
                      <Link href={`/invoices/${i.id}`}>
                        <div style={{ cursor: "pointer" }}>
                          <div style={{ fontWeight: 600 }}>{i.invoiceNumber ?? `#${i.id}`} — {i.title}</div>
                          <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>{i.customerName}</div>
                        </div>
                      </Link>
                      <div style={{ fontSize: "0.875rem", textTransform: "capitalize" }}>{i.status}</div>
                      <div style={{ fontSize: "0.875rem" }}>{i.invoiceDate ?? "—"}</div>
                      <div style={{ fontWeight: 600 }}>{formatCurrency(i.total)}</div>
                      <div style={{ fontWeight: 600, color: i.paid >= i.total ? "#10b981" : "#f59e0b" }}>{formatCurrency(i.paid)} paid</div>
                      <Button variant="ghost" size="icon" onClick={() => {
                        detachInvoice.mutate({ projectId, invoiceId: i.id }, { onSuccess: () => { toast({ title: "Detached" }); invalidate(); } });
                      }}><Unlink style={{ width: 14, height: 14 }} /></Button>
                    </CardContent>
                  </Card>
                  {children.map(child => {
                    const label = childTypeLabel(child);
                    return (
                      <div key={child.id} style={{ paddingLeft: "1.5rem", marginTop: "0.25rem" }}>
                        <Card style={{ borderStyle: "dashed", opacity: 0.9 }}>
                          <CardContent style={{ padding: "0.5rem 1rem", display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto", gap: "1rem", alignItems: "center" }}>
                            <Link href={`/invoices/${child.id}`}>
                              <div style={{ cursor: "pointer" }}>
                                <div style={{ fontWeight: 500, fontSize: "0.875rem" }}>
                                  {label && (
                                    <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", marginRight: 6, background: "hsl(var(--muted))", padding: "1px 6px", borderRadius: 4 }}>{label}</span>
                                  )}
                                  {child.invoiceNumber ?? `#${child.id}`} — {child.title}
                                </div>
                              </div>
                            </Link>
                            <div style={{ fontSize: "0.8125rem", textTransform: "capitalize", color: "hsl(var(--muted-foreground))" }}>{child.status}</div>
                            <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>{child.invoiceDate ?? "—"}</div>
                            <div style={{ fontSize: "0.875rem" }}>{formatCurrency(child.total)}</div>
                            <div style={{ fontSize: "0.875rem", color: child.paid >= child.total ? "#10b981" : "#f59e0b" }}>{formatCurrency(child.paid)} paid</div>
                            <div style={{ width: 32 }} />
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// ActivityTab
// ---------------------------------------------------------------------------

function ActivityTab({ projectId }: { projectId: number }) {
  const { data: events } = useProjectActivity(projectId);
  if (!events || events.length === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
        No activity yet.
      </div>
    );
  }
  const formatEventType = (type: string) =>
    type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div style={{ marginTop: "1.25rem", paddingLeft: "0.75rem" }}>
      <div style={{ position: "relative" }}>
        <div style={{
          position: "absolute",
          left: 7,
          top: 8,
          bottom: 8,
          width: 2,
          background: "hsl(var(--border))",
          borderRadius: 2,
        }} />
        <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
          {events.map((e, idx) => (
            <div key={e.id} style={{ display: "flex", gap: "1.25rem", paddingBottom: idx < events.length - 1 ? "1.25rem" : 0 }}>
              <div style={{ flexShrink: 0, position: "relative", zIndex: 1 }}>
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "hsl(var(--primary))",
                  border: "2px solid hsl(var(--background))",
                  boxShadow: "0 0 0 2px hsl(var(--primary) / 0.25)",
                  marginTop: 2,
                }} />
              </div>
              <div style={{ flex: 1, minWidth: 0, paddingBottom: "0.25rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>{formatEventType(e.type)}</span>
                  <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>
                    {new Date(e.createdAt).toLocaleString()}
                  </span>
                </div>
                {e.payload && Object.keys(e.payload).length > 0 && (
                  <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem" }}>
                    {Object.entries(e.payload).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChangeOrdersTab
// ---------------------------------------------------------------------------

// Select value for "create a new quote/invoice" in the attach dialog's target list.
const NEW_CO_TARGET = "new";

function ChangeOrdersTab({ projectId, projectName, customerId }: { projectId: number; projectName: string; customerId: number | null }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const { data: coEstimate, isLoading } = useGetProjectCoEstimate(projectId);
  const coEstimateId = coEstimate?.id ?? 0;
  const { data: estimate } = useGetEstimate(coEstimateId, {
    query: { queryKey: getGetEstimateQueryKey(coEstimateId), enabled: !!coEstimate?.id },
  });
  const { data: quotes } = useProjectQuotes(projectId);
  const { data: invoices } = useProjectInvoices(projectId);
  const attachMutation = useAttachCoItems();
  const createEstimate = useCreateEstimate();
  const createInvoice = useCreateInvoice();
  const attachQuote = useAttachQuote();
  const attachInvoice = useAttachInvoice();

  const [attachOpen, setAttachOpen] = useState(false);
  // selectedGroupIds: groups selected as a whole (attaches group + all items)
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  // selectedItemIds: individual ungrouped items selected
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  const [targetType, setTargetType] = useState<"estimate" | "invoice">("estimate");
  const [targetId, setTargetId] = useState<string>("");
  const defaultNewTitle = projectName ? `Change order — ${projectName}` : "Change order";
  const [newTitle, setNewTitle] = useState("");
  const isNewTarget = targetId === NEW_CO_TARGET;
  const targetLabel = targetType === "estimate" ? "quote" : "invoice";
  const isAttaching = attachMutation.isPending || createEstimate.isPending || createInvoice.isPending || attachQuote.isPending || attachInvoice.isPending;

  const handleTargetChange = (value: string) => {
    setTargetId(value);
    if (value === NEW_CO_TARGET && !newTitle) setNewTitle(defaultNewTitle);
  };

  const invalidateProjectDocs = () => {
    qc.invalidateQueries({ queryKey: getProjectQuotesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectInvoicesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectAggregatesQueryKey(projectId) });
    qc.invalidateQueries({ queryKey: getProjectDocumentLinksQueryKey() });
  };

  // Creates a draft quote/invoice for the project's customer and links it to
  // this project, like "New quote" / "New invoice" on the Billing tab does.
  // Returns the new document's id and a label for toasts, or null on failure.
  const createTargetForProject = async (): Promise<{ id: number; label: string } | null> => {
    if (!customerId) {
      toast({ title: "Assign a customer to this project first", variant: "destructive" });
      return null;
    }
    const title = newTitle.trim() || defaultNewTitle;
    let created: { id: number; label: string };
    try {
      if (targetType === "estimate") {
        const quote = await createEstimate.mutateAsync({ data: { customerId, title } });
        created = { id: quote.id, label: `quote ${quote.estimateNumber ? `#${quote.estimateNumber}` : `#${quote.id}`}` };
      } else {
        const invoice = await createInvoice.mutateAsync({ data: { customerId, title } });
        created = { id: invoice.id, label: `invoice ${invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : `#${invoice.id}`}` };
      }
    } catch {
      toast({ title: `Failed to create the new ${targetLabel}`, variant: "destructive" });
      return null;
    }
    try {
      if (targetType === "estimate") await attachQuote.mutateAsync({ projectId, quoteId: created.id });
      else await attachInvoice.mutateAsync({ projectId, invoiceId: created.id });
    } catch {
      toast({ title: `Created ${created.label}, but couldn't add it to this project. Attach it from the Billing tab.`, variant: "destructive" });
      return null;
    }
    invalidateProjectDocs();
    // From here on the new document is a normal target, so a retry after a
    // failed attach reuses it instead of creating another one.
    setTargetId(String(created.id));
    setNewTitle("");
    return created;
  };

  const sortedGroups = [...(estimate?.groups ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const allLineItems = estimate?.lineItems ?? [];
  const ungroupedItems = allLineItems.filter(i => !i.groupId);
  const hasContent = allLineItems.length > 0 || sortedGroups.length > 0;

  const totalSelected = selectedGroupIds.size + selectedItemIds.size;

  const handleSelectAll = () => {
    setSelectedGroupIds(new Set(sortedGroups.map(g => g.id)));
    setSelectedItemIds(new Set(ungroupedItems.map(i => i.id)));
  };
  const handleClearAll = () => {
    setSelectedGroupIds(new Set());
    setSelectedItemIds(new Set());
  };

  const handleAttach = async () => {
    if (totalSelected === 0) { toast({ title: "Select at least one item or group", variant: "destructive" }); return; }
    let id = parseInt(targetId, 10);
    let createdLabel: string | null = null;
    if (isNewTarget) {
      const created = await createTargetForProject();
      if (!created) return;
      id = created.id;
      createdLabel = created.label;
    }
    if (!id) { toast({ title: "Select a target quote or invoice", variant: "destructive" }); return; }
    attachMutation.mutate(
      {
        projectId,
        lineItemIds: Array.from(selectedItemIds),
        groupIds: Array.from(selectedGroupIds),
        targetEstimateId: targetType === "estimate" ? id : undefined,
        targetInvoiceId: targetType === "invoice" ? id : undefined,
      },
      {
        onSuccess: (result) => {
          const parts = [];
          if (selectedGroupIds.size > 0) parts.push(`${selectedGroupIds.size} group${selectedGroupIds.size === 1 ? "" : "s"}`);
          if (result.copied > 0) parts.push(`${result.copied} item${result.copied === 1 ? "" : "s"}`);
          toast({ title: createdLabel ? `Created ${createdLabel} and attached ${parts.join(" and ")}` : `Attached ${parts.join(" and ")}` });
          setAttachOpen(false);
          setSelectedGroupIds(new Set());
          setSelectedItemIds(new Set());
          setTargetId("");
          if (targetType === "estimate") qc.invalidateQueries({ queryKey: getGetEstimateQueryKey(id) });
          qc.invalidateQueries({ queryKey: getProjectQuotesQueryKey(projectId) });
          qc.invalidateQueries({ queryKey: getProjectInvoicesQueryKey(projectId) });
        },
        onError: () => toast({
          title: createdLabel ? `Created ${createdLabel}, but failed to attach items. Try Attach again.` : "Failed to attach items",
          variant: "destructive",
        }),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  if (!coEstimate?.id) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        No change order estimate found. Assign a customer to this project (next to the project name) to use Change Orders.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {hasContent && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setAttachOpen(true)}>
            <Send className="w-3.5 h-3.5 mr-2" />
            Attach to Quote / Invoice
          </Button>
        </div>
      )}

      <EstimateLineItemSection estimateId={coEstimate.id} />

      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Attach to Quote / Invoice</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Selection list: groups first, then ungrouped items */}
            <div>
              <p className="text-sm font-medium mb-2">Select what to attach</p>
              <div className="border rounded-md divide-y max-h-60 overflow-y-auto">
                {/* Groups */}
                {sortedGroups.map((group) => {
                  const groupItems = allLineItems.filter(i => i.groupId === group.id);
                  const groupTotal = groupItems.reduce((s, i) => s + Number(i.quantity) * Number(i.unitPrice), 0);
                  return (
                    <label key={group.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={selectedGroupIds.has(group.id)}
                        onChange={(e) => {
                          const next = new Set(selectedGroupIds);
                          if (e.target.checked) next.add(group.id); else next.delete(group.id);
                          setSelectedGroupIds(next);
                        }}
                      />
                      <div
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: group.accentColor || "hsl(var(--muted-foreground))" }}
                      />
                      <span className="flex-1 text-sm font-medium truncate">{group.name || "Untitled Group"}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {groupItems.length} item{groupItems.length === 1 ? "" : "s"} · {formatCurrency(groupTotal)}
                      </span>
                    </label>
                  );
                })}
                {/* Ungrouped items */}
                {ungroupedItems.map((item) => (
                  <label key={item.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={selectedItemIds.has(item.id)}
                      onChange={(e) => {
                        const next = new Set(selectedItemIds);
                        if (e.target.checked) next.add(item.id); else next.delete(item.id);
                        setSelectedItemIds(next);
                      }}
                    />
                    <span className="w-2.5 shrink-0" />
                    <span className="flex-1 text-sm truncate">{item.description}</span>
                    <span className="text-sm text-muted-foreground shrink-0">
                      {item.quantity} × {formatCurrency(Number(item.unitPrice))}
                    </span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 mt-1.5">
                <button type="button" className="text-xs text-primary hover:underline" onClick={handleSelectAll}>
                  Select all
                </button>
                <span className="text-xs text-muted-foreground">·</span>
                <button type="button" className="text-xs text-muted-foreground hover:underline" onClick={handleClearAll}>
                  Clear
                </button>
              </div>
            </div>

            {/* Target selection */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Attach to</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={targetType === "estimate" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setTargetType("estimate"); setTargetId(""); }}
                >
                  Quote
                </Button>
                <Button
                  type="button"
                  variant={targetType === "invoice" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setTargetType("invoice"); setTargetId(""); }}
                >
                  Invoice
                </Button>
              </div>
              <Select value={targetId} onValueChange={handleTargetChange}>
                <SelectTrigger>
                  <SelectValue placeholder={targetType === "estimate" ? "Select a quote…" : "Select an invoice…"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NEW_CO_TARGET} disabled={!customerId}>
                    <span className="flex items-center gap-1.5 font-medium text-primary">
                      <Plus className="w-3.5 h-3.5" />
                      New {targetLabel}
                      {!customerId && <span className="font-normal text-muted-foreground">(assign a customer to the project first)</span>}
                    </span>
                  </SelectItem>
                  {targetType === "estimate"
                    ? (quotes ?? []).map(q => (
                        <SelectItem key={q.id} value={String(q.id)}>
                          {q.estimateNumber ? `#${q.estimateNumber}` : `#${q.id}`} — {q.title}
                        </SelectItem>
                      ))
                    : (invoices ?? []).filter(i => !i.isDepositChild).map(i => (
                        <SelectItem key={i.id} value={String(i.id)}>
                          {i.invoiceNumber ? `#${i.invoiceNumber}` : `#${i.id}`} — {i.title}
                        </SelectItem>
                      ))
                  }
                </SelectContent>
              </Select>
              {isNewTarget && (
                <div className="space-y-1.5">
                  <Label htmlFor="co-new-target-title" className="text-xs text-muted-foreground">
                    Title for the new {targetLabel}
                  </Label>
                  <Input
                    id="co-new-target-title"
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                    placeholder={defaultNewTitle}
                  />
                  <p className="text-xs text-muted-foreground">
                    Created as a draft for this project's customer and added to this project.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setAttachOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAttach}
              disabled={isAttaching || totalSelected === 0 || !targetId}
            >
              {isAttaching && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {isNewTarget ? "Create & attach" : "Attach"}{totalSelected > 0 ? ` (${totalSelected})` : ""}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
